import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/styles/access.css';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Server-authoritative access gate.
 *
 * Reads the stored token from localStorage and validates it against
 * GET /api/access/validate/{token}. The server only accepts tokens that
 * have been properly redeemed ("used" state). Anything else — blank,
 * missing, unknown, unredeemed, or revoked — is rejected and the user
 * is redirected to /access. Fails closed.
 */
const PrivateGate = ({ children }) => {
  const [status, setStatus] = useState('checking'); // checking | valid | denied

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('nymphe_access_token');

    // Client-side guard: never even call the server with junk values.
    const looksValid =
      typeof token === 'string' &&
      token.trim().length > 0 &&
      token !== 'null' &&
      token !== 'undefined';

    if (!looksValid) {
      // Scrub any garbage so future loads don't keep failing closed noisily.
      try { localStorage.removeItem('nymphe_access_token'); } catch { /* ignore */ }
      setStatus('denied');
      return;
    }

    axios
      .get(`${API}/api/access/validate/${encodeURIComponent(token)}`)
      .then((res) => {
        if (cancelled) return;
        if (res.data && res.data.valid === true) {
          setStatus('valid');
        } else {
          try { localStorage.removeItem('nymphe_access_token'); } catch { /* ignore */ }
          setStatus('denied');
        }
      })
      .catch(() => {
        if (cancelled) return;
        try { localStorage.removeItem('nymphe_access_token'); } catch { /* ignore */ }
        setStatus('denied');
      });

    return () => { cancelled = true; };
  }, []);

  if (status === 'checking') {
    // Quiet dark placeholder — no copy leak about auth state.
    return (
      <div className="acc" data-testid="private-gate-checking">
        <div className="acc-bg">
          <img src="/images/access-bg.png" alt="" />
          <div className="acc-bg-overlay" />
        </div>
        <div className="noise-overlay" />
      </div>
    );
  }

  if (status === 'denied') {
    return <Navigate to="/access" replace data-testid="private-gate-denied" />;
  }

  return children;
};

export default PrivateGate;
